import { readAdminSession } from '../admin/authProvider';
import { readProjectSettings } from '../admin/adminSettings';

export type UltronInsightsSummary = {
    headerClicks: number;
    headerClickUsers: number;
    chatOpenedClicks: number;
    premiumRedirectClicks: number;
    chatTurns: number;
    chatUsers: number;
    successfulTurns: number;
    failedTurns: number;
    clickToChatRate: number;
    chatSuccessRate: number;
    averageTurnsPerChatUser: number;
};

export type UltronInsightsDay = {
    day: string;
    headerClicks: number;
    headerClickUsers: number;
    chatTurns: number;
    chatUsers: number;
};

export type UltronInsightsExam = {
    examName: string;
    headerClicks: number;
    headerClickUsers: number;
    chatTurns: number;
    chatUsers: number;
};

export type UltronEntryOutcome = {
    accessOutcome: string;
    clicks: number;
    users: number;
};

export type UltronRecentActivity = {
    activityId: string;
    activityType: string;
    userId: string;
    userName: string;
    email: string;
    outcome: string;
    source: string;
    platform: string;
    examName: string;
    occurredAtUtc: string;
};

export type UltronInsightsResult = {
    generatedAtUtc: string;
    fromUtc: string;
    toUtc: string;
    summary: UltronInsightsSummary;
    daily: UltronInsightsDay[];
    exams: UltronInsightsExam[];
    entryOutcomes: UltronEntryOutcome[];
    recentActivity: UltronRecentActivity[];
};

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function asString(value: unknown) {
    return value === null || value === undefined ? '' : String(value);
}

function asNumber(value: unknown) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function list(value: unknown) {
    return Array.isArray(value) ? value.map(asRecord) : [];
}

function errorMessage(payload: unknown, fallback: string) {
    if (typeof payload === 'string' && payload.trim()) return payload;
    const record = asRecord(payload);
    const value = record.errorMessage ?? record.message ?? record.error ?? record.title ?? record.detail;
    return typeof value === 'string' && value.trim() ? value : fallback;
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = String(
        import.meta.env.VITE_API_BASE_URL ??
        import.meta.env.VITE_API_URL ??
        settings.adminApiBaseUrl ??
        'https://test-api.pteultra.com/api/v1',
    ).replace(/\/+$/, '');
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );
    const apiRoot = /\/api\/v[^/]+$/i.test(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    return { apiRoot, adminToken };
}

function normalize(payload: unknown): UltronInsightsResult {
    const record = asRecord(payload);
    const summary = asRecord(record.summary);
    return {
        generatedAtUtc: asString(record.generatedAtUtc),
        fromUtc: asString(record.fromUtc),
        toUtc: asString(record.toUtc),
        summary: {
            headerClicks: asNumber(summary.headerClicks),
            headerClickUsers: asNumber(summary.headerClickUsers),
            chatOpenedClicks: asNumber(summary.chatOpenedClicks),
            premiumRedirectClicks: asNumber(summary.premiumRedirectClicks),
            chatTurns: asNumber(summary.chatTurns),
            chatUsers: asNumber(summary.chatUsers),
            successfulTurns: asNumber(summary.successfulTurns),
            failedTurns: asNumber(summary.failedTurns),
            clickToChatRate: asNumber(summary.clickToChatRate),
            chatSuccessRate: asNumber(summary.chatSuccessRate),
            averageTurnsPerChatUser: asNumber(summary.averageTurnsPerChatUser),
        },
        daily: list(record.daily).map((item) => ({
            day: asString(item.day),
            headerClicks: asNumber(item.headerClicks),
            headerClickUsers: asNumber(item.headerClickUsers),
            chatTurns: asNumber(item.chatTurns),
            chatUsers: asNumber(item.chatUsers),
        })),
        exams: list(record.exams).map((item) => ({
            examName: asString(item.examName),
            headerClicks: asNumber(item.headerClicks),
            headerClickUsers: asNumber(item.headerClickUsers),
            chatTurns: asNumber(item.chatTurns),
            chatUsers: asNumber(item.chatUsers),
        })),
        entryOutcomes: list(record.entryOutcomes).map((item) => ({
            accessOutcome: asString(item.accessOutcome),
            clicks: asNumber(item.clicks),
            users: asNumber(item.users),
        })),
        recentActivity: list(record.recentActivity).map((item) => ({
            activityId: asString(item.activityId),
            activityType: asString(item.activityType),
            userId: asString(item.userId),
            userName: asString(item.userName),
            email: asString(item.email),
            outcome: asString(item.outcome),
            source: asString(item.source),
            platform: asString(item.platform),
            examName: asString(item.examName),
            occurredAtUtc: asString(item.occurredAtUtc),
        })),
    };
}

export async function getUltronInsights(fromUtc: string, toUtc: string): Promise<UltronInsightsResult> {
    const { apiRoot, adminToken } = getAdminApiConfig();
    const url = new URL(`${apiRoot}/AdminUltronAnalytics`);
    url.searchParams.set('fromUtc', fromUtc);
    url.searchParams.set('toUtc', toUtc);

    const response = await fetch(url, {
        headers: {
            Accept: 'application/json',
            ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
        },
    });
    const text = await response.text();
    let payload: unknown = null;
    if (text) {
        try {
            payload = JSON.parse(text);
        } catch {
            payload = text;
        }
    }
    if (!response.ok) {
        throw new Error(errorMessage(payload, `Ultron insights request failed (${response.status})`));
    }

    const envelope = asRecord(payload) as AdminApiEnvelope<unknown>;
    if (envelope.success === false) {
        throw new Error(errorMessage(envelope, 'Ultron insights request failed'));
    }
    return normalize(envelope.data ?? payload);
}
